/**
 * The Action Queue cards for GET /dashboard/action-queue — every non-casing
 * vertical's items (downhole incl. Directional & MWD, trucking, surface
 * rentals, pressure testing, NDT). Casing's cards are still buildActionAlerts'.
 *
 * The server decides WHAT needs action, pluralises the label and names the
 * screen; nothing here counts or recomputes. What this file owns is how each
 * fact is said:
 *
 *   - MONEY PER CURRENCY. `amounts` is one entry per currency and is never
 *     summed here either: Apex Directional TX bills USD and AB bills CAD, and
 *     one figure across them is a number nobody billed.
 *   - UNPRICED IS SAID AS UNPRICED. `unpriced_count` rows carry no figure; they
 *     read "12 unpriced", never $0 and never folded silently into the total.
 *   - A FAILURE IS A FAILURE CARD. A whole-route failure, and each item the
 *     server reports in `failures`, draws a neutral failure card — never an
 *     absence, so the queue cannot read "nothing needs action" over a read
 *     that did not arrive.
 */
import type { KeyedAlert } from './buildActionAlerts';
import type { OpsAlertTone } from '../dashboard/OpsAlerts';
import { fmtAmount } from '../../lib/currency-amounts';
import { loadFailureMessage, type LoadFailureKind } from '../../lib/load-failure';
import type {
  ActionQueue,
  ActionQueueAmount,
  ActionQueueSeverity,
  ActionQueueVertical,
} from '../../types/action-queue';

/**
 * Exhaustive over the route's verticals. The home route is where a failure
 * card for that vertical goes — a failure carries no link of its own. An
 * unknown key (a vertical added server-side first) gets a neutral name and
 * the Overview itself, never a guess at another vertical's screen.
 */
const VERTICAL: Record<ActionQueueVertical, { label: string; home: string }> = {
  downhole_tools: { label: 'Downhole', home: '/downhole' },
  trucking: { label: 'Trucking', home: '/trucking' },
  surface_rentals: { label: 'Surface Rentals', home: '/rentals' },
  pressure_testing: { label: 'Pressure Testing', home: '/pressure-testing' },
  ndt_inspection: { label: 'NDT', home: '/ndt-inspection' },
  pipeline_facility: { label: 'Pipeline', home: '/pipeline-facility' },
  well_testing: { label: 'Well Testing', home: '/well-testing' },
  well_servicing: { label: 'Well Servicing', home: '/well-servicing' },
};
const UNKNOWN_VERTICAL = { label: 'Other', home: '/' };
const verticalOf = (v: string) => VERTICAL[v as ActionQueueVertical] ?? UNKNOWN_VERTICAL;

/** Exhaustive over the route's severities; an unknown one claims no colour. */
const TONE: Record<ActionQueueSeverity, OpsAlertTone> = { error: 'red', warn: 'amber', info: 'blue' };
const SEVERITY_ORDER: Record<ActionQueueSeverity, number> = { error: 0, warn: 1, info: 2 };

/** "US$7,175.00 · CA$1,200.00 · 3 unpriced", or '' when the item has no money. */
export function moneyDetail(amounts: ActionQueueAmount[], unpricedCount: number): string {
  const parts = amounts.map(fmtAmount);
  if (unpricedCount > 0) parts.push(`${unpricedCount} unpriced`);
  return parts.join(' · ');
}

const joinLabels = (labels: string[]) =>
  labels.length <= 1 ? labels.join('') : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;

export interface ServerActionAlertInputs {
  /** The route's answer; undefined while loading, or when it did not arrive. */
  queue: ActionQueue | null | undefined;
  /** The route's own failure, from queryLoadFailure. */
  failure: LoadFailureKind | null;
  /** The company's verticals this route serves, for naming a whole-route failure. */
  verticals: ActionQueueVertical[];
}

export function buildServerActionAlerts(i: ServerActionAlertInputs): KeyedAlert[] {
  if (i.failure) {
    const first = verticalOf(i.verticals[0] ?? '');
    const { heading } = loadFailureMessage(
      i.failure,
      `${joinLabels(i.verticals.map((v) => verticalOf(v).label))} items`,
    );
    return [{
      key: `server-failure:${i.failure}`,
      label: heading,
      detail: i.failure === 'refused'
        ? 'Not a statement about whether anything needs action'
        : 'Not a statement that nothing needs action. Try again in a moment',
      action: 'Open',
      path: first.home,
      failure: i.failure,
    }];
  }
  if (!i.queue) return [];

  const alerts: KeyedAlert[] = [];

  // Item failures, one card per vertical: the route answered, these items did not.
  const failedBy = new Map<string, number>();
  for (const f of i.queue.failures ?? []) failedBy.set(f.vertical, (failedBy.get(f.vertical) ?? 0) + 1);
  failedBy.forEach((n, vertical) => {
    const v = verticalOf(vertical);
    alerts.push({
      key: `server-item-failure:${vertical}`,
      label: `Couldn’t load ${n} ${v.label} ${n === 1 ? 'item' : 'items'}`,
      detail: 'Not a statement that nothing needs action. Try again in a moment',
      action: 'Open',
      path: v.home,
      failure: 'unreachable',
    });
  });

  const items = [...(i.queue.items ?? [])].sort(
    (a, b) => (SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3),
  );
  for (const item of items) {
    alerts.push({
      key: `server:${item.kind}`,
      label: item.label,
      detail: moneyDetail(item.amounts ?? [], item.unpriced_count ?? 0),
      action: 'Open',
      path: item.link,
      tone: TONE[item.severity] ?? 'neutral',
    });
  }
  return alerts;
}
